import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Image, Alert } from 'react-native';
import { TextInput, Button, Text, useTheme, HelperText, ActivityIndicator, Snackbar, Avatar } from 'react-native-paper';
import { useAuth } from '../../../context/AuthContext';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const EditProfileScreen = ({ navigation }) => {
  const theme = useTheme();
  const { user, updateUserInfo } = useAuth();
  
  // Form state
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setSurname(user.surname || '');
      setEmail(user.email || '');
      setPhone(user.phone_number || '');
      setProfileImage(user.profile_image || null);
    }
  }, [user]);
  
  // İlk harfi büyük yapma yardımcı işlevi
  const getInitials = () => {
    if (name && surname) {
      return `${name.charAt(0).toUpperCase()}`;
      //return `${name.charAt(0).toUpperCase()}${surname.charAt(0).toUpperCase()}`;
    } else if (name) {
      return `${name.charAt(0).toUpperCase()}`;
      //return name.charAt(0).toUpperCase();
    } else if (user && user.username) {
      return `${name.charAt(0).toUpperCase()}`;
      //return user.username.charAt(0).toUpperCase();
    }
    return '?';
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Ad alanı gereklidir';
    }
    
    if (!surname.trim()) {
      newErrors.surname = 'Soyad alanı gereklidir';
    }
    
    if (!email.trim()) {
      newErrors.email = 'E-posta alanı gereklidir';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
    }
    
    if (phone && !/^(\+90|0)?\s*[0-9]{3}\s*[0-9]{3}\s*[0-9]{2}\s*[0-9]{2}$/.test(phone.replace(/\s+/g, ''))) {
      newErrors.phone_number = 'Geçerli bir telefon numarası giriniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Profil güncellemesi için AuthContext'teki fonksiyonu kullanıyoruz
      const updatedData = {
        name,
        surname,
        email,
        phone_number: phone,
        profile_image: profileImage
      };
      
      await updateUserInfo(updatedData);
      
      setSnackbarMessage('Profil başarıyla güncellendi');
      setSnackbarVisible(true);
      
      // Kısa bir süre bekleyip profil ekranına dön
      setTimeout(() => {
        navigation.goBack();
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbarMessage('Profil güncellenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePickImage = () => {
    // Gerçek uygulamada burada expo-image-picker veya benzer bir kütüphane kullanılır
    // Bu örnek için sadece bir uyarı gösteriyoruz
    Alert.alert(
      'Resim Seç',
      'Bu özellik bu versiyonda henüz aktif değildir.',
      [{ text: 'Tamam', style: 'cancel' }]
    );
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.formContainer}>
        {/* Profil Resmi */}
        <View style={styles.imageContainer}>
          <Avatar.Text 
            size={120} 
            label={getInitials()} 
            style={{ backgroundColor: 'rgb(125, 76, 236)' }}
            color="white"
          />
        </View>
        
        {/* Ad */}
        <TextInput
          label="Ad *"
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
          error={!!errors.name}
          disabled={loading}
          outlineColor="rgb(125, 76, 236)"
          activeOutlineColor="rgb(125, 76, 236)"
        />
        {errors.name && <HelperText type="error">{errors.name}</HelperText>}
        
        {/* Soyad */}
        <TextInput
          label="Soyad *"
          value={surname}
          onChangeText={setSurname}
          mode="outlined"
          style={styles.input}
          error={!!errors.surname}
          disabled={loading}
          outlineColor="rgb(125, 76, 236)"
          activeOutlineColor="rgb(125, 76, 236)"
        />
        {errors.surname && <HelperText type="error">{errors.surname}</HelperText>}
        
        {/* E-posta */}
        <TextInput
          label="E-posta *"
          value={email}
          onChangeText={setEmail}
          mode="outlined"
          keyboardType="email-address"
          style={styles.input}
          error={!!errors.email}
          disabled={loading}
          outlineColor="rgb(125, 76, 236)"
          activeOutlineColor="rgb(125, 76, 236)"
        />
        {errors.email && <HelperText type="error">{errors.email}</HelperText>}
        
        {/* Telefon */}
        <TextInput
          label="Telefon"
          value={phone}
          onChangeText={setPhone}
          mode="outlined"
          keyboardType="phone-pad"
          style={styles.input}
          error={!!errors.phone_number}
          disabled={loading}
          outlineColor="rgb(125, 76, 236)"
          activeOutlineColor="rgb(125, 76, 236)"
        />
        {errors.phone_number && <HelperText type="error">{errors.phone_number}</HelperText>}
        
        {/* Not */}
        <Text style={styles.noteText}>
          * işaretli alanların doldurulması zorunludur.
        </Text>
        
        {/* Kaydet Butonu */}
        <Button
          mode="contained"
          onPress={handleSave}
          style={[styles.saveButton, {backgroundColor: 'rgb(125, 76, 236)'}]}
          disabled={loading}
          labelStyle={{color: 'white', fontWeight: 'bold'}}
        >
          {loading ? (
            <ActivityIndicator color="white" size="small" />
          ) : (
            <Text style={{color: 'white'}}>Kaydet</Text>
          )}
        </Button>
        
        {/* İptal Butonu */}
        <Button
          mode="outlined"
          onPress={() => navigation.goBack()}
          style={[styles.cancelButton, {borderColor: 'rgb(125, 76, 236)'}]}
          disabled={loading}
          textColor="rgb(125, 76, 236)"
        >
          İptal
        </Button>
      </View>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  formContainer: {
    padding: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 8,
    margin: 8,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  input: {
    marginBottom: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
  },
  noteText: {
    color: 'rgb(125, 76, 236)',
    fontSize: 12,
    marginBottom: 16,
    fontStyle: 'italic',
    fontWeight: 'bold',
  },
  saveButton: {
    marginTop: 16,
    paddingVertical: 8,
  },
  cancelButton: {
    marginTop: 12,
    paddingVertical: 8,
  },
});

export default EditProfileScreen;
